
const namespace = 'retryState';

const DEFAULT_OPTIONS = {
    retries: 3,
    isError: false,
    statusCodes: [],
    methods: ['get'],
    retryCondition: _ => true,
    retryDelay: _ => 2000, // 2s
    middleware: config => config,
    onRetry: _ => {},
    validateResponse: null,
};

const setRetryState = (config, reset = false, options = {}) => {
    const retryState = config[namespace] || {};
    retryState.count = retryState.count || 0;
    
    // Only set retries from default if not already set and no options provided
    if (!retryState.retries) {
        retryState.retries = options.retries || DEFAULT_OPTIONS.retries;
    }

    if (!retryState.lastRequest || reset) {
        retryState.lastRequest = Date.now();
    }

    if (reset) {
        retryState.isLastAttempt = false;
    }

    config[namespace] = retryState;

    return retryState;
};

const getRequestOptions = (config, options = {}) => {
    return Object.assign({}, DEFAULT_OPTIONS, options, config[namespace]);
};

const fixConfig = (axios, config) => {
    if (axios.defaults.agent === config.agent) {
      delete config.agent;
    }
    if (axios.defaults.httpAgent === config.httpAgent) {
      delete config.httpAgent;
    }
    if (axios.defaults.httpsAgent === config.httpsAgent) {
      delete config.httpsAgent;
    }
};

// delay strategies
export const retryStrategies = {
    multiply: ({ count = 0 } = {}) => {
        return count * 1000;
    },
    exponential: ({ count = 0 } = {}) => {
        const delay = Math.pow(2, count) * 100;
         // 0-20% of the delay
        const randomSum = delay * 0.2 * Math.random();
        return delay + randomSum;
    }
};

export const retryRequest = (axios, options = {}) => {
    const requestInterceptorId = axios.interceptors.request.use(config => {
        const retryState = setRetryState(config, true, options);

        retryState.isLastAttempt = (retryState.count || 0) >= (retryState.retries || 0);

        if (config[namespace]?.validateResponse) {
            // by setting this, all HTTP responses will be go through the error interceptor first
            config.validateStatus = () => false;
        }

        return config;
    });

    const interceptor = async (payload) => {
        const response = payload.response || payload;
        const { config, status = 0 } = response;

        if (!config) {
            return Promise.reject(payload);
        }

        // options
        const {
            retries,
            retryDelay,
            retryCondition,
            statusCodes,
            middleware,
            methods,
            isError,
            onRetry,
        } = getRequestOptions(config, options);

        if (statusCodes.includes(status) && methods.includes(config.method)) {
            const state = setRetryState(config, false, options);
            const shouldRetry = retryCondition(payload) && state.count < retries;

            if (shouldRetry) {
                state.count += 1;
                const delay = retryDelay(state);

                // Axios fails merging this configuration to the default configuration because it has an issue
                // with circular structures: https://github.com/mzabriskie/axios/issues/370
                fixConfig(axios, config);

                config.transformRequest = [data => data];

                if (config.signal?.aborted) {
                    return Promise.resolve(axios(config));
                }

                await onRetry(state, config);
                return new Promise(resolve => {
                    setTimeout(_ => {
                        resolve(axios(middleware(config)))
                    }, delay)
                });
            }
        }

        return isError ? Promise.reject(payload) : payload;
    }

    // interceptors
    const [success, fail] = options.isError ? [null, interceptor] : [interceptor, null];

    // register interceptors
    const responseInterceptorId =axios.interceptors.response.use(success, fail);

    return { requestInterceptorId, responseInterceptorId };
};
